import {useEffect, useState} from "react";
import {
  TextButton,
  FlexBox,
  Modal,
  Title,
  Body,
  Button,
  IconButton, Loader,
} from "components";
import { StyledHorizontalRule, StyledDownloadingLoader} from "./FileCourierReceivedDetailsModal.module";
import { FileCourierReceivedDetailsModalProps } from "interfaces";
import { formatDate } from "utils/dateFunctions";
import {
  downloadPackage,
  downloadFile,
} from "utils/fileCourier";
import {
  useLazyGetFileCourierPackageUrlQuery,
  useLazyGetFileCourierFileUrlQuery, setNotification
} from "services";
import {useDispatch} from "../../hooks/redux";


const FileCourierReceivedDetailsModal = ({
  receivedPackage,
  onClose,
  onRefresh,
}: FileCourierReceivedDetailsModalProps) => {
  const {
    id,
    sentDate,
    expirationDate,
    sentFromFirstName,
    sentFromLastName,
    files } = receivedPackage;
  
  const handleClose = () => {
    onClose();
    onRefresh();
  };
  
  const dispatch = useDispatch();
  
  const [getPackageUrl, {data: packageUrl, isLoading: packageUrlLoading, isSuccess: packageUrlSuccess, isError: packageUrlError}] = useLazyGetFileCourierPackageUrlQuery();
  
  const [getFileUrl, {data: fileUrl, isLoading: fileUrlLoading, isSuccess: fileUrlSuccess, isError: fileUrlError}] = useLazyGetFileCourierFileUrlQuery();
  
  const [selectedFileId, _selectedFileId] = useState<string | null>(null);
  
  const callGetPackageUrl = (packageId: string) => {
    getPackageUrl(packageId)
    .unwrap()
    .catch((e: any) => {
      dispatch(
        setNotification({
          type: "error",
          message: "Could not fetch package url for download",
        })
      );
      console.error(e);
    });
  };
  
  const callGetFileUrl = (fileId: string) => {
    getFileUrl(fileId)
    .unwrap()
    .catch((e: any) => {
      dispatch(
        setNotification({
          type: "error",
          message: "Could not fetch file url for download",
        })
      );
      console.error(e);
    });
  };
  
  const downloadFileAndResetSelectedFile = async (): Promise<void> => {
    if (fileUrl && fileUrl.trim().length > 0) {
      await downloadFile(fileUrl);
      _selectedFileId(null);
    }
  };
  
  useEffect(() => {
    if (fileUrl) {
      downloadFileAndResetSelectedFile();
    }
  }, [fileUrl]);
  
  useEffect(() => {
    if (packageUrl) {
      downloadPackage(packageUrl);
    }
  }, [packageUrl]);
  
  const handleDownloadPackage = async (packageId: string) => {
    await callGetPackageUrl(packageId);
  }

  const handleDownloadFile = async (fileId: string) => {
    _selectedFileId(fileId);
    await callGetFileUrl(fileId);
  }

  return (
    <Modal testId="fc-received-details-modal" open={true} onClose={handleClose}>
      <FlexBox gap="1.25rem" minWidth="100%">
        <Title size="large" bold>
          Files received {formatDate(sentDate)}
        </Title>
        <Title size="medium">
          Expires {formatDate(expirationDate)}
        </Title>
        <Title size="medium">
          From: {sentFromFirstName} {sentFromLastName}
        </Title>
        {!packageUrlLoading && (
          <FlexBox pd="0 0 2.5rem 0">
            <FlexBox row pd="1rem 0 .1rem 0" align="flex-end" justify="space-between">
              <Body size="large">
                File name
              </Body>
              <Button
                type="submit"
                use="primary"
                ariaLabel="download package"
                size="small"
                disabled={packageUrlLoading}
                data-testid="download-all-button"
                onClick={() => handleDownloadPackage(id)}
              >
                Download All
              </Button>
            </FlexBox>
            <StyledHorizontalRule />
            {files.map((file) => (
              <FlexBox key={file.id} row pd="0 0 .5rem 0" justify="space-between">
                <Body size="large" bold>
                  {file.name}
                </Body>
                {(selectedFileId === file.id) && (
                  <StyledDownloadingLoader active containerHeight="1.5rem" />
                )}
                {!(selectedFileId === file.id) && (
                  <IconButton
                    key={file.id}
                    name="download"
                    size="large"
                    ariaLabel="download file"
                    disabled={fileUrlLoading}
                    onClick={() => handleDownloadFile(file.id)}
                    addTooltip
                    testId="download-file-button"
                    toolTipText="download"
                  />
                )}
              </FlexBox>
            ))}
          </FlexBox>
        )}
        {packageUrlLoading && (
          <FlexBox align="center" justify="center">
            <Loader active containerHeight="10rem" />
          </FlexBox>
        )}
        <FlexBox row gap="1.5rem" align="center" justify="flex-start">
          <TextButton data-testid="cancel-button" onClick={handleClose}>Cancel</TextButton>
        </FlexBox>
      </FlexBox>
    </Modal>
  );
};

export default FileCourierReceivedDetailsModal;